import { create } from 'zustand';
import type { FinalSong } from '../interfaces/final-song';
import type { CuratedPlaylist } from '../interfaces/curated-playlist';
import { generateRandomString } from '../shared/utils/random';

// Port of src/app/services/global-state.service.ts.
//
// NOTE(port): playlistSongs was `Set<FinalSong>` in Angular, with membership
// decided by object reference identity (`set.has(song)`). Re-rendering in React
// makes reference identity fragile (songs are re-created on immutable updates),
// so it is ported as `Map<string, FinalSong>` keyed by a stable song key:
// `song.id` when present, otherwise `${title}|${artist}`. The toggle semantics
// of addRemoveSongFromPlaylist (add if absent, remove if present, return
// whether the song was added) are preserved exactly.
export function songKey(song: FinalSong): string {
  return song.id ? song.id : `${song.title}|${song.artist}`;
}

export function getDefaultNoMoreResultsMessage(): string {
  return 'Nothing to see here!<br>\n' +
    '          Your search seems to be too specific to return any results. Loosen a few search filters or read more about\n' +
    '          why this might happen\n' +
    '          <a class="underline hover:text-mypart_red-500" href="https://www.mypart.net/songhunt-faq"\n' +
    '             target="_blank">here.</a>';
}

interface GlobalState {
  // --- signals → state fields ---
  playlistSongs: Map<string, FinalSong>;
  referenceSongFromUrl: number | undefined;
  highlightedSong: FinalSong | undefined;
  signUpSvg: string | undefined;

  showNoMoreResults: boolean;
  noMoreResultsMessage: string;
  curatedPlaylists: CuratedPlaylist[] | undefined;
  playAllSongs: boolean;
  logoLocationLink: string;
  songs: FinalSong[];
  initialLoadingProcessComplete: boolean;
  viewOverride: 'home' | 'results' | null;
  setViewOverride: (value: 'home' | 'results' | null) => void;
  /** One-shot signal: when true, the next router-navigation handler call keeps the
   *  current viewOverride (instead of clearing it) and resets this back to false.
   *  Lets a view-override survive the single navigation it was set to bridge — e.g.
   *  clearSearchBar's forceStayOnResults navigating to a param-less URL — while every
   *  other, genuinely separate navigation still clears the override as before. */
  viewOverrideSticky: boolean;
  setViewOverrideSticky: (value: boolean) => void;
  showPlaylist: boolean;
  showAdminPanel: boolean;
  showProfilePage: boolean;
  songsLoading: boolean;
  loadMoreSongsEvent: boolean;
  scrollEventOnMobile: boolean;
  filterWasClickedDontHideFilters: boolean;
  discoveryMode: boolean;
  appTheme: 'dark' | 'light';
  semanticsModel: 'SimCSE' | 'Roberta';
  resultItemWidth: number;
  resultItemHeight: number;

  // --- plain (non-signal) mutable fields in Angular → state fields here ---
  hideCloseButtonCounter: number;
  errorRetrievingPlaylists: boolean;
  artistPages: number;
  resultsPageNumber: number;
  totalTimesNoSongsFound: number;
  totalPlaylistSongs: number;
  whiteLabelProjectIds: number[];
  publicProjectIds: number[];
  projectIdsToUse: string;
  // Angular held an ElementRef to the nav bar; in React store the DOM element.
  navBar: HTMLElement | null;

  // Random-string re-render trigger. Angular exposed a getter Signal and a
  // setter that ignored its argument and set a fresh random string; components
  // subscribe to this field to re-render. Call updateGlobalState() to trigger.
  updateGlobalStateSignal: string;

  // --- actions ---
  updateGlobalState: () => void;
  initAppTheme: () => void;
  setSongs: (songs: FinalSong[]) => void;
  setSongsLyricsMatch: (finalSongs: FinalSong[]) => void;
  addRemoveSongFromPlaylist: (song: FinalSong) => boolean;
  addTopTwentySongsToPlaylist: () => void;
  isSongInPlaylist: (song: FinalSong) => boolean;
  getSongsForYoutube: () => string[];
  getSongsForSpotify: () => { artist: string; title: string }[];
  addPageNumber: () => void;
  resetPageNumber: () => void;
  resetPlaylist: () => void;
  getPageNumForCall: () => string;
  removeSongAddedToPlaylist: (song: FinalSong) => void;
  getArtworkIdsFromPlaylist: () => string[];
  setSongsLoading: (loading: boolean) => void;

  // Generic setter for fields Angular code assigned directly
  // (e.g. `globalStateService.projectIdsToUse = ...`).
  setGlobalField: <K extends keyof GlobalState>(key: K, value: GlobalState[K]) => void;
}

export const useGlobalStore = create<GlobalState>()((set, get) => ({
  playlistSongs: new Map<string, FinalSong>(),
  referenceSongFromUrl: undefined,
  highlightedSong: undefined,
  signUpSvg: undefined,

  showNoMoreResults: false,
  noMoreResultsMessage: getDefaultNoMoreResultsMessage(),
  curatedPlaylists: undefined,
  playAllSongs: false,
  logoLocationLink: '',
  songs: [],
  initialLoadingProcessComplete: false,
  viewOverride: null,
  setViewOverride: (value) => set({ viewOverride: value }),
  viewOverrideSticky: false,
  setViewOverrideSticky: (value) => set({ viewOverrideSticky: value }),
  showPlaylist: false,
  showAdminPanel: false,
  showProfilePage: false,
  songsLoading: true,
  loadMoreSongsEvent: false,
  scrollEventOnMobile: false,
  filterWasClickedDontHideFilters: false,
  discoveryMode: false,
  appTheme: 'light',
  semanticsModel: 'SimCSE',
  resultItemWidth: 240,
  resultItemHeight: 0,

  hideCloseButtonCounter: 0,
  errorRetrievingPlaylists: false,
  artistPages: -1,
  resultsPageNumber: 0,
  totalTimesNoSongsFound: 0,
  totalPlaylistSongs: 0,
  whiteLabelProjectIds: [],
  publicProjectIds: [],
  projectIdsToUse: '',
  navBar: null,

  updateGlobalStateSignal: generateRandomString(),

  updateGlobalState: () => set({ updateGlobalStateSignal: generateRandomString() }),

  initAppTheme: () => {
    const theme = localStorage.getItem('theme');
    const appTheme = (localStorage.getItem('theme')
      || (window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
      )) as 'dark' | 'light';
    set({ appTheme });
    if (theme) {
      localStorage.setItem('theme', appTheme);
    }
  },

  // Angular wrapped this in setTimeout (defer past current change-detection
  // cycle) — preserved. Page 0 replaces the list; later pages append.
  setSongs: (songs) => {
    setTimeout(() => {
      if (get().resultsPageNumber === 0) {
        set({ songs }); // Directly set new songs
      } else {
        set(state => ({ songs: [...state.songs, ...songs] })); // Append new songs
      }
    });
  },

  setSongsLyricsMatch: (finalSongs) => {
    set(state => ({
      songs: state.songs.map(currentSong => {
        // Find the corresponding song in finalSongs using the unique identifier (e.g., id)
        const matchingFinalSong = finalSongs.find(finalSong => finalSong.id === currentSong.id);

        // If a matching song is found, merge its properties with the current song
        return matchingFinalSong ? { ...currentSong, lyricsMatch: matchingFinalSong.lyricsMatch } : currentSong;
      })
    }));
  },

  addRemoveSongFromPlaylist: (song) => {
    let songAddedToPlaylist = false;
    const updated = new Map(get().playlistSongs);
    const key = songKey(song);
    if (updated.has(key)) {
      updated.delete(key);
    } else {
      updated.set(key, song);
      songAddedToPlaylist = true;
    }
    set({ playlistSongs: updated, totalPlaylistSongs: updated.size });
    return songAddedToPlaylist;
  },

  addTopTwentySongsToPlaylist: () => {
    // Get the current songs and playlist songs
    const allSongs = get().songs;
    const playlistSongs = get().playlistSongs;

    // Create a set of IDs for songs already in the playlist for faster lookup
    const playlistSongIds = new Set([...playlistSongs.values()].map(song => song.id));

    // Take the first 20 songs and filter out the ones already added
    const songsToAdd = allSongs.slice(0, 20).filter(song =>
      !song.songAddedToPlaylist && !playlistSongIds.has(song.id)
    );

    if (songsToAdd.length === 0) return;

    // Mark added songs in `songs`
    const updatedSongs = allSongs.map(song =>
      songsToAdd.some(s => s.id === song.id) ? { ...song, songAddedToPlaylist: true } : song
    );

    // Create a new Map to avoid mutating the existing one
    const updatedPlaylistSongs = new Map(playlistSongs);
    songsToAdd.forEach(song => updatedPlaylistSongs.set(songKey(song), song));

    // NOTE(port): Angular did not update totalPlaylistSongs here (only
    // addRemoveSongFromPlaylist did) — behavior preserved.
    set({ songs: updatedSongs, playlistSongs: updatedPlaylistSongs });
  },

  isSongInPlaylist: (song) => get().playlistSongs.has(songKey(song)),

  getSongsForYoutube: () => {
    return Array.from(get().playlistSongs.values()).map(song => song.youtube_id);
  },

  getSongsForSpotify: () => {
    return Array.from(get().playlistSongs.values()).map(song => ({ artist: song.artist, title: song.title }));
  },

  addPageNumber: () => set(state => ({ resultsPageNumber: state.resultsPageNumber + 1 })),

  resetPageNumber: () => set({ totalTimesNoSongsFound: 0, resultsPageNumber: 0 }),

  resetPlaylist: () => set({ playlistSongs: new Map<string, FinalSong>() }),

  getPageNumForCall: () => get().resultsPageNumber.toString(),

  removeSongAddedToPlaylist: (song) => {
    set(state => ({
      songs: state.songs.map(s =>
        s.youtube_id === song.youtube_id ? { ...s, songAddedToPlaylist: false } : s
      )
    }));
  },

  getArtworkIdsFromPlaylist: () => {
    const songs = Array.from(get().playlistSongs.values());
    return songs.map(song => song.id); // Replace 'id' with the appropriate property for artworkId
  },

  setSongsLoading: (loading) => set({ songsLoading: loading }),

  setGlobalField: (key, value) => set({ [key]: value } as Pick<GlobalState, typeof key>),
}));
